import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';

import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AccordionPanelComponent, AccordionModule } from 'ngx-bootstrap/accordion';
import MicroModal from 'micromodal';
import { Store } from '@ngxs/store';
import { get } from 'lodash';
import { ConfigurationState, LaunchDarklyService, UserService } from 'remarketing-angular-library';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { CheckUnsavedForms } from '../../interfaces/check-unsaved-forms';
import { CheckUnsavedFormsDirective } from '../../directives/check-unsaved-forms.directive';
import { VehiclesService } from '../../../vehicles/services/vehicle-detail/vehicles.service';
import { ServiceCarfaxComponent } from '../service-carfax/service-carfax.component';
import { AutoCheckComponent } from '../auto-check/auto-check.component';
import { AlertsOthersComponent } from '../alerts-others/alerts-others.component';
import { AlertsSellersComponent } from '../alerts-sellers/alerts-sellers.component';
import { AlertsBuyersComponent } from '../alerts-buyers/alerts-buyers.component';
import { AlertsAlertsComponent } from '../alerts-alerts/alerts-alerts.component';
import { DealerSellerComponent } from '../dealer-seller/dealer-seller.component';
import { DealerCreditComponent } from '../dealer-credit/dealer-credit.component';
import { DealerContactComponent } from '../dealer-contact/dealer-contact.component';
import { ChangePasswordComponent } from '../change-password/change-password.component';

import { UserContactComponent } from '../user-contact/user-contact.component';
import { TranslocoDirective } from '@jsverse/transloco';

@Component({
    selector: 'app-profile-mobile',
    templateUrl: './profile-mobile.component.html',
    styleUrls: ['./profile-mobile.component.scss'],
    imports: [TranslocoDirective, AccordionModule, UserContactComponent, CheckUnsavedFormsDirective, ChangePasswordComponent, DealerContactComponent, DealerCreditComponent, DealerSellerComponent, AlertsAlertsComponent, AlertsBuyersComponent, AlertsSellersComponent, AlertsOthersComponent, AutoCheckComponent, ServiceCarfaxComponent]
})
export class ProfileMobileComponent extends SaComponent implements OnInit, CheckUnsavedForms {
  @ViewChildren(CheckUnsavedFormsDirective) formComponents: QueryList<CheckUnsavedFormsDirective>;
  @ViewChildren(AccordionPanelComponent) accordionPanels: QueryList<AccordionPanelComponent>;

  modalId = '';

  isDisabled = false;

  clickedPanelClass: string;
  closingAccordion = false;
  acknowledged = false;
  private sessionStorageKey = 'ibubble_alertsMoved';

  tenantName: string;

  accordionState = {
    'user-contact': false,
    'change-password': false,
    'dealer-contact': false,
    'dealer-credit': false,
    'dealer-seller': false,
    'alerts-alerts' :false,
    'alerts-users': false,
    'alerts-sellers': false,
    'alerts-others': false,
    'autocheck-user-id': false,
    'car-fax': false
  };

  get isVehicleHistoryEnabled(): boolean {
    return this.vehicleService.isVehicleHistoryReportEnabled;
  }

  get isCanadianTenantEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get isEligibleToPurchase(): boolean {
    return get(this.store.snapshot(), 'org.organization.organizationProfile.eligibleToPurchase');
  }

  get isEligibleToSell(): boolean {
    return get(this.store.snapshot(), 'org.organization.organizationProfile.eligibleToSell');
  }

  get dealershipSellerLinkEnabled(): boolean {
    return this.isEligibleToSell && this._launchDarklyService.getFeatureFlag('profile-dealershipSellerLinkEnabled', false);
  }

  get dealershipBuyerLinkEnabled(): boolean {
    return this.isEligibleToPurchase && this._launchDarklyService.getFeatureFlag('profile-dealershipBuyerLinkEnabled', false);
  }

  get isManageAlertsEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('profile-manageAlertsEnabled', false);
  }

  get isViewOnly() {
    return this.store.selectSnapshot(ConfigurationState.hasRole('View_Only'));
  }

  get enableEditSecurityProfile(): boolean {
    return this._launchDarklyService.getFeatureFlag('profile-editSecurityProfileEnabled', false);
  }

  get isCanadianAmbassador() {
    return this.userService.isCanadianAmbassador();
  }

  get enableManageServices() {
    return this.isVehicleHistoryEnabled && !this.isViewOnly && !this.isCanadianAmbassador;
  }

  get isEditSecurityProfileEnabled() {
    return this.enableEditSecurityProfile || this.isCanadianAmbassador;
  }

  constructor(
    private readonly vehicleService: VehiclesService,
    private readonly store: Store,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly userService: UserService
  ) {super()}

  ngOnInit() {
    this.tenantName = get(this.store.selectSnapshot(ConfigurationState.configuration), 'tenantName', null);
    this.acknowledged = sessionStorage.getItem(this.sessionStorageKey) === 'true';
  }

  hasUnsavedForms(): boolean {
    return this.formComponents.some(form => form.ref.hasUnsavedForms());
  }

  onBeforeUnload(event: BeforeUnloadEvent): void {
    // Blank as each component handles this itself
  }

  showAlert() {
    // do whatever action is expected when you are moving away from unsaved changes
  }

  showNavigationAlert(): Observable<boolean> {
    const childAlerts$ = this.formComponents
      .filter(form => form.ref.hasUnsavedForms())
      .map(form => form.ref.showNavigationAlert());
    return combineLatest(of(true), ...childAlerts$).pipe(
      map(results => {
        return results.every(v => v);
      })
    );
  }

  // need to disabled the correct accordion when the child components are edited
  clickAccordion(panelClass: string) {
    this.clickedPanelClass = panelClass;
    const openAccordion = this.accordionPanels.find(accordionPanel => accordionPanel.isOpen);
    const accordion = this.accordionPanels.find(accordionPanel => accordionPanel.panelClass === panelClass);

    // if there are any unsaved forms open the modal for that form
    if (this.hasUnsavedForms()) {
      this.closingAccordion = true;
      const component = this.formComponents.find(form => form.ref.hasUnsavedForms());
      component.ref.showAlert();
      return;
    }

    // if there is no openAccordion or if the openAccordion is the same one we are clicking,
    // then toggle the open state
    if (!openAccordion || (openAccordion && openAccordion.panelClass === panelClass)) {
      accordion.isOpen = !accordion.isOpen;
      this.closingAccordion = !accordion.isOpen;
      this.accordionState[panelClass] = accordion.isOpen;
      return;
    }

    // if there is an openAccordion and it isn't the one we are clicking...
    // close the open one and open the clicked accordion
    if (openAccordion && openAccordion.panelClass !== panelClass) {
      openAccordion.isOpen = false;
      this.closingAccordion = false;
      accordion.isOpen = true;
      this.accordionState[panelClass] = true;
    }
  }

  openMicroModal(modalId: string) {
    MicroModal.show(modalId, {
      disableScroll: true
    });
    const heading = document.querySelector(`#${modalId} h1`) as HTMLElement;
    if (heading) {
      heading.focus();
    }
  }

  isAlertsPageEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('profile-alertsPageEnabled', false);
  }

  destroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  close() {
    this.acknowledged = true;
    sessionStorage.setItem(this.sessionStorageKey, 'true');
    this.destroy();
  }

  closeAccordion(panelClass: string) {
    if (this.closingAccordion) {
      if (this.clickedPanelClass !== panelClass) {
        const accordion = this.accordionPanels.find(accordionPanel => accordionPanel.panelClass === panelClass);
        accordion.isOpen = false;
        const clickedAccordion = this.accordionPanels.find(accordionPanel => accordionPanel.panelClass === this.clickedPanelClass);
        clickedAccordion.isOpen = true;
      } else {
        const accordion = this.accordionPanels.find(accordionPanel => accordionPanel.panelClass === panelClass);
        accordion.isOpen = false;
      }
    }
    this.clickedPanelClass = '';
  }

  shouldDisplayAlertNotice() {
    return (this.isAlertsPageEnabled() && !this.acknowledged) && this.isManageAlertsEnabled;
  }
}
